import { CommonModule } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { AppStreamEnvironment, ErmSdkModule } from 'erm-sdk';
import { EnvironmentServicesComponent } from './environment-services.component';
import { EnvironmentLaunchComponent } from './environment-launch.component';
import { Router, RouterModule } from '@angular/router';
import { EnvironmentUiService } from './services/environment-ui.service';

@Component({
  standalone: true,
  imports: [CommonModule, ErmSdkModule, EnvironmentServicesComponent, EnvironmentLaunchComponent, RouterModule],
  selector: 'app-environment',
  providers: [EnvironmentUiService],
  template: `
@if (uiService.environment$()) {
<article
  class="tw-flex tw-flex-col tw-justify-between tw-gap-1 tw-bg-white tw-rounded-2xl tw-shadow tw-border tw-border-gray-200 tw-p-3 tw-w-full tw-max-w-[1024px] tw-h-full"
>
  <!-- Top row -->
  <div class="tw-flex tw-items-start tw-justify-between tw-gap-4">
    <div class="tw-flex tw-items-center tw-gap-4">
      <!-- App icon -->
      <div
        class="tw-h-16 tw-w-16 tw-rounded-xl tw-bg-[#4b2e83] tw-flex tw-items-center tw-justify-center tw-text-white"
        aria-hidden="true"
      >
        <img src="assets/imgs/aveva-erm-logo-64x64.png" class="tw-h-16" />
      </div>

      <div class="tw-space-y-0.5">
        <h2 class="tw-text-base tw-font-semibold tw-text-slate-900">
          AVEVA ERM v{{ uiService.environment$().version }}
        </h2>
        <div class="tw-text-sm tw-text-slate-500">{{ uiService.environment$().name }}</div>
        <div class="tw-text-sm tw-text-slate-700">{{ uiService.environment$().description }}</div>
      </div>
    </div>

    <!-- Read More button -->
    <button
      type="button"
      class="tw-inline-flex tw-items-center tw-justify-center tw-font-semibold tw-text-white tw-bg-indigo-600 hover:tw-bg-indigo-700 tw-rounded-md tw-px-4 tw-py-2 tw-shadow-sm tw-transition"
      (click)="readMoreButtonClick()"
    >
      Read More
    </button>
  </div>

  <div class="tw-flex tw-flex-row tw-items-center tw-justify-between tw-gap-2">
    <!-- Labels -->
    <div class="tw-flex tw-flex-row tw-gap-1">
      @for (label of uiService.environment$()?.labels; track label) {
        <span class="tw-text-xs tw-text-slate-500 tw-bg-slate-100 tw-rounded-md tw-px-2 tw-py-1" [attr.title]="label.description">{{label.labelName}}</span>
      }
      @if (uiService.environment$()?.isTelemetryEnabled) {
        <span class="tw-text-xs tw-text-slate-500 tw-bg-slate-100 tw-rounded-md tw-px-2 tw-py-1" [attr.title]="'Telemetry'">OTEL</span>
      }
    </div>

    <!-- Status -->
    <div class="tw-flex tw-flex-row tw-gap-2">
      <lib-env-status class="tw-text-sm" [env]="uiService.environment$()"></lib-env-status>
    </div>
  </div>

  <!-- Bottom panels -->
  <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-2 tw-gap-4">
    <!-- Services panel -->
    <app-environment-services></app-environment-services>

    <!-- Environment panel -->
    <app-environment-launch></app-environment-launch>
  </div>
</article>
}
@else {
  <div class="tw-flex tw-items-center tw-justify-center tw-h-full">
    <p>No environment provided</p>
  </div>
}
`,
})
export class EnvironmentComponent {
  private readonly router = inject(Router);
  public readonly uiService = inject(EnvironmentUiService);

  @Input() set environment(environment: AppStreamEnvironment) {
    this.uiService.setEnvironment(environment);
  }

  public readMoreButtonClick(): void {
    this.router.navigate(['/erm/info', this.uiService.environment$()?.environmentId]);
  }
}
